import { useCallback, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Archive, Clock, Pencil, Plus, RotateCw, Shapes, Sparkles } from "lucide-react";
import { MAX_PRODUCT_AREAS, type GroomingEngineErrorCode } from "@ttc-spectree/toolbox-contract";
import { Chip } from "@/components/grooming/grooming-chips";
import { QueueBadge, queueBadgeStatus, safeQueueKey, useOnQueueSettled } from "@/components/grooming/queue-status-badge";
import { RunnerFallback } from "@/components/grooming/runner-fallback";
import {
  PROJECT_GHOST_ACTION as ghostAction,
  PROJECT_PRIMARY_BUTTON as primaryButton,
  PROJECT_TABLE_HEAD as tableHead,
} from "@/components/grooming/project/project-styles";
import { ErrorLine, type ErrorLineFix } from "@/components/toolbox/error-line";
import { UndoNotice } from "@/components/toolbox/undo-notice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  groomingKeys,
  useArchiveProductArea,
  useCreateProductArea,
  useProductAreas,
  useRestoreProductArea,
  useUpdateProductArea,
} from "@/hooks/queries/use-grooming";
import { useGroomingQueue } from "@/hooks/use-grooming-queue";
import { useUndoNotice } from "@/hooks/use-undo-notice";
import { ApiError } from "@/lib/api/client";
import type { GroomingConfig, ProductArea, ProductAreaInput } from "@/lib/api/grooming";
import { projectPath } from "@/lib/grooming/paths";
import { quoteArg } from "@/lib/grooming/plain-words";
import { useRunnerBridge } from "@/lib/grooming/runner-bridge";
import { useToolboxBridge } from "@/lib/toolbox/bridge";
import { fixItForPause, runFixIt } from "@/lib/toolbox/fix-it";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { cn } from "@/lib/utils";

/**
 * The Project page's Product areas tab (PKG-PM-F; feature PI-SPEC-SW-1510
 * moved it from the old Repos page, feature PI-SPEC-SW-1512 restyled it to
 * Claude Design "PM Packages B E F Mockups" frames F2 and F3; UX-PM-16,
 * UX-PM-34): the plain names every brief uses.
 *
 * - Empty (F2): what product areas are, and Claude's draft as the main
 *   button ("Draft from code"); adding one by hand is the quiet second
 *   choice. The empty tab never says "Redraft": nothing was drafted yet.
 * - With areas (F3): how many, who drafted and edited them and that edited
 *   names survive a redraft; "Redraft from code" (same action as Draft from
 *   code) and "Add area"; an "edited" chip on the rows a person changed.
 * - Remove acts at once and shows an Undo notice for UNDO_NOTICE_MS (never a
 *   confirm dialog); Undo restores the area with the source it had before.
 * - A failed draft, save or Undo is an ErrorLine that stays until it is
 *   fixed, retried successfully or closed; a closed failed queue run stays
 *   closed on later visits (remembered per project on this computer). A draft that failed because the
 *   repositories need a sign-in offers "Sign in to the repos" (PKG-PM-B's
 *   Fix it helper), then drafts again.
 *
 * In a contract-3 app (epic PI-SPEC-SW-E284) the draft goes to the app's run
 * queue and shows its "Queued · #n" / "Running" badge; elsewhere in the app
 * it calls `draftProductAreas` directly; a browser shows the slash command.
 */

const inputClassName = "h-8 text-sm";

/** The table's columns (frame F3): Area · Where in the code · Used in briefs · actions. */
const areaGrid = "grid grid-cols-[minmax(0,1.1fr)_minmax(0,1.3fr)_110px_150px] items-center gap-3";

function AreaForm({
  initial,
  submitLabel,
  pending,
  onSubmit,
  onCancel,
}: {
  initial: ProductAreaInput;
  submitLabel: string;
  pending: boolean;
  onSubmit: (input: ProductAreaInput) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial.name);
  const [description, setDescription] = useState(initial.description);
  const [codeHint, setCodeHint] = useState(initial.codeHint ?? "");

  return (
    <form
      className="flex flex-wrap items-end gap-3 px-[18px] py-3"
      data-testid="area-form"
      onSubmit={(event) => {
        event.preventDefault();
        if (!name.trim() || !description.trim()) return;
        onSubmit({ name: name.trim(), description: description.trim(), codeHint: codeHint.trim() || null });
      }}
    >
      <label className="flex min-w-[180px] flex-1 flex-col gap-1 text-xs text-muted-foreground">
        Area name
        <Input className={inputClassName} value={name} maxLength={120} onChange={(e) => { setName(e.target.value); }} required />
      </label>
      <label className="flex min-w-[220px] flex-[2] flex-col gap-1 text-xs text-muted-foreground">
        In plain words
        <Input className={inputClassName} value={description} maxLength={500} onChange={(e) => { setDescription(e.target.value); }} required />
      </label>
      <label className="flex min-w-[160px] flex-1 flex-col gap-1 text-xs text-muted-foreground">
        Where in the code (optional)
        <Input className={inputClassName} value={codeHint} maxLength={500} onChange={(e) => { setCodeHint(e.target.value); }} />
      </label>
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={pending || !name.trim() || !description.trim()}>
          {pending ? "Saving…" : submitLabel}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Words (pure; exported for tests)
// ---------------------------------------------------------------------------

/** "a", "a and b", "a, b and c". */
export function joinWithAnd(words: readonly string[]): string {
  if (words.length <= 1) return words[0] ?? "";
  return `${words.slice(0, -1).join(", ")} and ${words[words.length - 1] ?? ""}`;
}

/** Ends a reason with a full stop when it has no closing punctuation. */
function asSentence(text: string): string {
  const trimmed = text.trim();
  return /[.!?…]$/.test(trimmed) ? trimmed : `${trimmed}.`;
}

/** "Oct 14", with the year when it is not this year. */
function shortDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", ...(sameYear ? {} : { year: "numeric" }) });
}

/**
 * The summary line above the table (frame F3): "7 areas · drafted by Claude
 * on Oct 14, edited by Tony Truong. Names you edited stay when Claude
 * redrafts." The date is the newest Claude-drafted row's; the names are the
 * people who last changed a person-edited row. Parts that do not apply are
 * left out.
 */
export function areasSummary(areas: readonly ProductArea[]): string {
  const count = `${String(areas.length)} ${areas.length === 1 ? "area" : "areas"}`;
  const latestAi = areas
    .filter((a) => a.source === "ai")
    .reduce<string | null>((max, a) => (max === null || a.createdAt > max ? a.createdAt : max), null);
  const editors = [
    ...new Set(
      areas
        .filter((a) => a.source === "person")
        .map((a) => a.updatedByName?.trim() ?? "")
        .filter((name) => name.length > 0)
    ),
  ];
  const parts: string[] = [];
  if (latestAi) parts.push(`drafted by Claude on ${shortDate(latestAi)}`);
  if (editors.length > 0) parts.push(`edited by ${joinWithAnd(editors)}`);
  const facts = parts.length > 0 ? ` · ${parts.join(", ")}` : "";
  return `${count}${facts}. Names you edited stay when Claude redrafts.`;
}

/** What a failed draft did not change: "Nothing changed: your 7 areas are as they were." */
export function nothingChanged(count: number): string {
  if (count === 0) return "Nothing changed.";
  if (count === 1) return "Nothing changed: your 1 area is as it was.";
  return `Nothing changed: your ${String(count)} areas are as they were.`;
}

/** The empty state's repository list: "icentral-nt and icentral-nt-web". */
function repoList(repos: readonly { repo: string }[]): string {
  return repos.length > 0 ? joinWithAnd(repos.map((r) => r.repo)) : "the project's repositories";
}

/** The API's reason for a refused write, in its own words; a plain fallback when it gave none. */
export function apiReason(error: unknown): string {
  if (error instanceof ApiError && error.message && !error.message.startsWith("API Error:")) {
    return asSentence(error.message);
  }
  return "SpecTree did not answer. Check your connection and try again.";
}

const SIGN_IN_WORDS = /sign(?:ed)?[ -]?in/i;
const REPO_WORDS = /\b(?:repo|repos|repository|repositories|code|azure devops|git)\b/i;

/** True when a failed draft needs a sign-in to the repositories (its code, or its words when there is no code). */
export function needsRepoSignIn(failure: { message: string; errorCode?: GroomingEngineErrorCode | undefined }): boolean {
  if (failure.errorCode) {
    return fixItForPause({ reason: "setup", errorCode: failure.errorCode })?.action === "repo-sign-in";
  }
  return SIGN_IN_WORDS.test(failure.message) && REPO_WORDS.test(failure.message);
}

// ---------------------------------------------------------------------------
// The draft
// ---------------------------------------------------------------------------

interface RedraftFailure {
  message: string;
  errorCode?: GroomingEngineErrorCode | undefined;
}

/** localStorage key prefix of the closed failed draft run, per project. */
export const CLOSED_DRAFT_FAILURE_STORAGE_KEY = "spectree.grooming.closedAreasDraftFailure";

/**
 * The failed queue run whose ErrorLine the person closed on this project
 * (PKG-PM-F Mac walk, F-W2): the run stays under the Queue's Finished
 * today, so without this the line came back on every visit. Per computer,
 * like the How it's going panel; null when none is stored or storage is
 * unavailable.
 */
export function readClosedDraftFailure(project: string): string | null {
  try {
    return window.localStorage.getItem(`${CLOSED_DRAFT_FAILURE_STORAGE_KEY}.${project}`);
  } catch {
    return null;
  }
}

/** Remember the closed run; a storage failure only means it is closed for this visit. */
function writeClosedDraftFailure(project: string, entryId: string): void {
  try {
    window.localStorage.setItem(`${CLOSED_DRAFT_FAILURE_STORAGE_KEY}.${project}`, entryId);
  } catch {
    // Not remembered; the line stays closed while the page is open.
  }
}

/**
 * Draft (or redraft) from code: the action, its status lines and its
 * failure. Both paths stay: the run queue in a contract-3 app, the direct
 * `draftProductAreas` call in an older one.
 */
function useRedraftAction(project: string, repos: readonly { repo: string }[], hasAreas: boolean) {
  const { runner, support } = useRunnerBridge();
  const queryClient = useQueryClient();
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const [failure, setFailure] = useState<RedraftFailure | null>(null);
  const [dismissedQueueEntry, setDismissedQueueEntry] = useState<string | null>(() => readClosedDraftFailure(project));
  const [dismissedProject, setDismissedProject] = useState(project);
  if (dismissedProject !== project) {
    setDismissedProject(project);
    setDismissedQueueEntry(readClosedDraftFailure(project));
  }
  // E284: a contract-3 app drafts through its run queue (one product-areas entry per project).
  const { supported: queueSupported, state: queueState, actions: queueActions } = useGroomingQueue();
  const queueMode = queueSupported && runner !== null;
  const queueStatus = queueMode ? queueBadgeStatus(queueState, safeQueueKey("product-areas", project), Date.now()) : null;
  const queueActive = queueStatus?.kind === "queued" || queueStatus?.kind === "running" ? queueStatus : null;
  useOnQueueSettled(queueActive?.entry.id ?? null, () => {
    void queryClient.invalidateQueries({ queryKey: groomingKeys.productAreas(project) });
  });

  const redraft = useCallback(() => {
    if (!runner) return;
    setDone(false);
    if (queueMode) {
      void queueActions.enqueueProductAreas(project).then((result) => {
        if (result.ok) setFailure(null);
        else setFailure({ message: result.message, errorCode: result.errorCode });
      });
      return;
    }
    setRunning(true);
    void runner.draftProductAreas(project).then(
      (result) => {
        setRunning(false);
        if (result.ok) {
          setFailure(null);
          setDone(true);
          void queryClient.invalidateQueries({ queryKey: groomingKeys.productAreas(project) });
        } else {
          setFailure({ message: result.message, errorCode: result.errorCode });
        }
      },
      () => {
        setRunning(false);
        setFailure({ message: "SpecTree Toolbox could not draft the areas." });
      }
    );
  }, [runner, queueMode, queueActions, project, queryClient]);

  const queueFailed = queueStatus?.kind === "failed" && queueStatus.entry.id !== dismissedQueueEntry ? queueStatus.entry : null;
  const shownFailure: RedraftFailure | null =
    failure ??
    (queueFailed
      ? { message: queueFailed.message ?? "Claude could not draft the areas.", errorCode: queueFailed.errorCode }
      : null);

  const dismiss = useCallback(() => {
    setFailure(null);
    if (queueStatus?.kind === "failed") {
      setDismissedQueueEntry(queueStatus.entry.id);
      writeClosedDraftFailure(project, queueStatus.entry.id);
    }
  }, [queueStatus, project]);

  const repoArgs = repos.length > 0 ? repos.map((r) => `./${r.repo}`).join(",") : "<path to your copy>";
  const reading = queueActive?.kind === "running" || running;
  const drafted = queueStatus?.kind === "ready" || done;

  return {
    runner,
    queueMode,
    queuePaused: queueState?.status === "paused",
    resumeQueue: () => queueActions.resume(),
    redraft,
    running,
    queueActive,
    failure: shownFailure,
    dismiss,
    status: (
      <>
        {reading && (
          <p className={cn(TOOLBOX_TEXT.content, "text-muted-foreground")} role="status">
            {hasAreas ? "Claude is reading the code. Areas people edited are kept." : "Claude is reading the code."}
          </p>
        )}
        {drafted && !reading && (
          <p className={cn(TOOLBOX_TEXT.content, "text-green-400")} role="status">
            {hasAreas ? "Claude drafted the areas. Areas people edited were kept." : "Claude drafted the areas."}
          </p>
        )}
        <RunnerFallback
          support={support}
          command={{
            label: "Or draft the areas in Claude Code",
            command: `/spectree:ado-product-areas ${quoteArg(project)} --repos ${repoArgs}`,
            helperText:
              "Run this in Claude Code from the folder that holds your copies of these repositories, or change the paths. Areas people edited are kept.",
          }}
        />
      </>
    ),
  };
}

// ---------------------------------------------------------------------------
// The tab
// ---------------------------------------------------------------------------

/** A failed save, or a failed Undo, kept on screen until it is dealt with. */
interface SaveError {
  title: string;
  cause: string;
  retry?: () => void;
}

/**
 * Project → Product areas (`map-project`): frames F2 (empty) and F3 (with
 * areas), Remove with Undo, and errors that stay (feature PI-SPEC-SW-1512).
 */
export function ProductAreasTab({ project, config }: { project: string; config: GroomingConfig | undefined }) {
  const areasQuery = useProductAreas(project);
  const create = useCreateProductArea(project);
  const update = useUpdateProductArea(project);
  const archive = useArchiveProductArea(project);
  const restore = useRestoreProductArea(project);
  const undoNotice = useUndoNotice();
  const bridge = useToolboxBridge();
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<SaveError | null>(null);
  const [fixing, setFixing] = useState(false);
  const [fixMessage, setFixMessage] = useState<string | null>(null);
  const removals = useRef(0);
  const repos = config?.repos ?? [];
  const areas = areasQuery.data ?? [];
  const hasAreas = areas.length > 0;
  const draft = useRedraftAction(project, repos, hasAreas);
  const atLimit = areas.length >= MAX_PRODUCT_AREAS;

  const failSave = (retry: () => void) => (error: unknown) => {
    setSaveError({ title: "The change was not saved.", cause: apiReason(error), retry });
  };

  const addArea = (input: ProductAreaInput) => {
    create.mutate(input, {
      onSuccess: () => {
        setAdding(false);
        setSaveError(null);
      },
      onError: failSave(() => {
        addArea(input);
      }),
    });
  };

  const editArea = (areaId: string, input: ProductAreaInput) => {
    update.mutate(
      { areaId, input },
      {
        onSuccess: () => {
          setEditing(null);
          setSaveError(null);
        },
        onError: failSave(() => {
          editArea(areaId, input);
        }),
      }
    );
  };

  /** Remove at once, then offer Undo; Undo restores the area with the source it had before. */
  const removeArea = (area: ProductArea) => {
    const { id, name, source } = area;
    undoNotice.clearError();
    archive.mutate(id, {
      onSuccess: () => {
        setSaveError(null);
        removals.current += 1;
        undoNotice.show({
          id: `area-${id}-${String(removals.current)}`,
          icon: Archive,
          message: (
            <>
              Removed <b className="font-semibold">&quot;{name}&quot;</b>. Briefs written so far keep the name.
            </>
          ),
          undo: async () => {
            await restore.mutateAsync({ areaId: id, source });
          },
        });
      },
      onError: failSave(() => {
        removeArea(area);
      }),
    });
  };

  /** Draft again, starting from a clean slate for the fix's own message. */
  const retryDraft = () => {
    setFixMessage(null);
    draft.redraft();
  };

  const closeDraftError = () => {
    setFixMessage(null);
    draft.dismiss();
  };

  /** "Sign in to the repos": PKG-PM-B's Fix it for a repository sign-in, then the draft again. */
  const signInFix: ErrorLineFix | undefined =
    draft.failure && needsRepoSignIn(draft.failure) && bridge && draft.runner
      ? {
          label: fixing ? "Signing in…" : "Sign in to the repos",
          onClick: () => {
            if (fixing) return;
            setFixing(true);
            void runFixIt(
              { action: "repo-sign-in", setupStep: null },
              {
                bridge,
                project,
                navigate: (to) => {
                  void navigate(to);
                },
                // Only a queue paused for setup needs starting again; otherwise there is nothing to resume.
                resume: () =>
                  draft.queueMode && draft.queuePaused ? draft.resumeQueue() : Promise.resolve({ ok: true as const }),
              }
            ).then((result) => {
              setFixing(false);
              if (result.kind === "failed") {
                setFixMessage(result.message);
                return;
              }
              closeDraftError();
              draft.redraft();
            });
          },
        }
      : undefined;

  const errors = (
    <>
      {draft.failure && (
        <ErrorLine
          title={hasAreas ? "The redraft didn't finish." : "The draft didn't finish."}
          cause={`${asSentence(fixMessage ?? draft.failure.message)} ${nothingChanged(areas.length)}`}
          {...(signInFix ? { fix: signInFix } : {})}
          onRetry={retryDraft}
          onClose={closeDraftError}
          className="w-full"
        />
      )}
      {saveError && (
        <ErrorLine
          title={saveError.title}
          cause={saveError.cause}
          {...(saveError.retry ? { onRetry: saveError.retry } : {})}
          onClose={() => {
            setSaveError(null);
          }}
          className="w-full"
        />
      )}
      {undoNotice.error && (
        <ErrorLine
          title="The area was not put back."
          cause={apiReason(undoNotice.error)}
          onClose={undoNotice.clearError}
          className="w-full"
        />
      )}
      {undoNotice.notice && (
        <UndoNotice
          key={undoNotice.notice.id}
          {...(undoNotice.notice.icon ? { icon: undoNotice.notice.icon } : {})}
          message={undoNotice.notice.message}
          pending={undoNotice.pending}
          onUndo={() => {
            void undoNotice.undo();
          }}
          onExpire={() => {
            undoNotice.dismiss(undoNotice.notice?.id);
          }}
        />
      )}
    </>
  );

  if (areasQuery.isLoading) {
    return (
      <div className="flex flex-col gap-3.5" data-testid="repos-areas">
        <p className={cn(TOOLBOX_TEXT.content, "text-muted-foreground")}>Loading…</p>
      </div>
    );
  }

  if (areasQuery.isError) {
    return (
      <div className="flex flex-col gap-3.5" data-testid="repos-areas">
        <p className={cn(TOOLBOX_TEXT.content, "text-destructive")} role="alert" data-testid="areas-load-error">
          The product areas could not be loaded. Try again in a moment.
        </p>
      </div>
    );
  }

  if (!hasAreas) {
    // Frame F2: Claude's draft is the main action; the word "Redraft" never appears here.
    return (
      <div className="flex flex-col items-center gap-4 pt-2" data-testid="repos-areas">
        {errors}
        <div
          className="glass-card flex w-[640px] max-w-full flex-col items-center gap-4 rounded-[10px] px-9 py-8 text-center"
          data-testid="areas-empty"
        >
          <span className="flex h-[52px] w-[52px] items-center justify-center rounded-[13px] border border-[rgba(35,164,255,0.3)] bg-[rgba(35,164,255,0.12)]">
            <Shapes className="h-[26px] w-[26px] text-[#6CC4FF]" aria-hidden="true" />
          </span>
          <h2 className="font-display text-lg font-semibold leading-snug">Let Claude draft your product areas</h2>
          <p className="text-sm leading-relaxed text-muted-foreground" data-testid="areas-empty-body">
            Briefs name parts of the product in plain words, like{" "}
            <b className="font-semibold text-foreground">&quot;Station schedules&quot;</b> or{" "}
            <b className="font-semibold text-foreground">&quot;Weather alerts&quot;</b>, instead of file names. Claude reads{" "}
            {repoList(repos)} and proposes 8 to 15 of them.
          </p>
          <div className={cn(TOOLBOX_TEXT.content, "flex flex-wrap justify-center gap-4")}>
            <span className="flex items-center gap-1.5">
              <Clock className={cn("h-[15px] w-[15px]", TOOLBOX_TEXT.faint)} aria-hidden="true" />
              About 5 minutes, in the Queue
            </span>
            <span className="flex items-center gap-1.5">
              <Pencil className={cn("h-[15px] w-[15px]", TOOLBOX_TEXT.faint)} aria-hidden="true" />
              Rename, merge or remove any of them after
            </span>
          </div>
          {draft.queueActive ? (
            <QueueBadge status={draft.queueActive} />
          ) : (
            <Button
              type="button"
              className={cn(primaryButton, "h-9 px-4 text-sm")}
              disabled={!draft.runner || draft.running}
              onClick={draft.redraft}
            >
              <Sparkles className={cn(draft.running && "animate-pulse")} aria-hidden="true" />
              {draft.running ? "Drafting…" : "Draft from code"}
            </Button>
          )}
          <button
            type="button"
            className={ghostAction}
            onClick={() => {
              setAdding((value) => !value);
            }}
          >
            Or add one yourself
          </button>
        </div>
        {adding && (
          <div className="glass-card w-[640px] max-w-full rounded-[10px]">
            <AreaForm
              initial={{ name: "", description: "", codeHint: null }}
              submitLabel="Add area"
              pending={create.isPending}
              onCancel={() => {
                setAdding(false);
              }}
              onSubmit={addArea}
            />
          </div>
        )}
        <div className="flex w-[640px] max-w-full flex-col gap-3">{draft.status}</div>
        <p className={cn(TOOLBOX_TEXT.meta, TOOLBOX_TEXT.faint, "w-[640px] max-w-full text-center")}>
          Uses the repositories your Admin set up (
          <Link to={projectPath(project, "repositories")} className="text-link hover:underline">
            Repositories tab
          </Link>
          ).
        </p>
      </div>
    );
  }

  // Frame F3.
  return (
    <div className="flex flex-col gap-3" data-testid="repos-areas">
      {errors}
      <div className="flex flex-wrap items-center gap-2.5">
        <p className={cn(TOOLBOX_TEXT.content, "min-w-0 flex-1 leading-relaxed text-muted-foreground")} data-testid="areas-summary">
          {areasSummary(areas)}
        </p>
        {draft.queueActive ? (
          <QueueBadge status={draft.queueActive} />
        ) : (
          <Button type="button" size="sm" variant="outline" disabled={!draft.runner || draft.running} onClick={draft.redraft}>
            <RotateCw className={cn(draft.running && "animate-spin")} aria-hidden="true" />
            {draft.running ? "Drafting…" : "Redraft from code"}
          </Button>
        )}
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={atLimit}
          title={atLimit ? `A project has at most ${String(MAX_PRODUCT_AREAS)} product areas.` : undefined}
          onClick={() => {
            setAdding((value) => !value);
          }}
        >
          <Plus aria-hidden="true" />
          Add area
        </Button>
      </div>
      {draft.status}

      <div className="glass-card overflow-x-auto rounded-[10px]">
        <div className="min-w-[640px]">
          <div className={cn(areaGrid, tableHead)}>
            <span>Area</span>
            <span>Where in the code</span>
            <span>Used in briefs</span>
            <span />
          </div>
          {adding && (
            <div className="border-b border-[#141417]">
              <AreaForm
                initial={{ name: "", description: "", codeHint: null }}
                submitLabel="Add area"
                pending={create.isPending}
                onCancel={() => {
                  setAdding(false);
                }}
                onSubmit={addArea}
              />
            </div>
          )}
          <ul>
            {areas.map((area) =>
              editing === area.id ? (
                <li key={area.id} className="border-b border-[#141417] last:border-b-0">
                  <AreaForm
                    initial={{ name: area.name, description: area.description, codeHint: area.codeHint }}
                    submitLabel="Save"
                    pending={update.isPending}
                    onCancel={() => {
                      setEditing(null);
                    }}
                    onSubmit={(input) => {
                      editArea(area.id, input);
                    }}
                  />
                </li>
              ) : (
                <li
                  key={area.id}
                  className={cn(areaGrid, "border-b border-[#141417] px-[18px] py-3 last:border-b-0")}
                  data-testid="area-row"
                >
                  <div className="flex min-w-0 flex-col gap-0.5">
                    <span className="flex min-w-0 items-center gap-1.5 font-medium">
                      <span className="truncate">{area.name}</span>
                      {area.source === "person" && (
                        <Chip tone="none" testId="area-edited">
                          edited
                        </Chip>
                      )}
                    </span>
                    <span className="text-[13px] text-muted-foreground">{area.description}</span>
                  </div>
                  <span className="truncate text-sm text-muted-foreground">{area.codeHint ?? "—"}</span>
                  <span className="text-sm text-muted-foreground">
                    {area.usedInBriefs} {area.usedInBriefs === 1 ? "brief" : "briefs"}
                  </span>
                  <span className="flex justify-end gap-1">
                    <button
                      type="button"
                      className={ghostAction}
                      onClick={() => {
                        setEditing(area.id);
                      }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className={ghostAction}
                      disabled={archive.isPending}
                      onClick={() => {
                        removeArea(area);
                      }}
                    >
                      Remove
                    </button>
                  </span>
                </li>
              )
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}
